import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AvailabilityCalendar } from '@/components/interview/AvailabilityCalendar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface TimeSlot {
  date: string;
  startTime: string;
  endTime: string;
}

interface TestInfo {
  title: string;
  job_title: string;
  organization: string;
  location: string;
}

export const InterviewAvailability: React.FC = () => {
  const { responseId } = useParams<{ responseId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuth();

  const [testInfo, setTestInfo] = useState<TestInfo | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<TimeSlot[]>([]);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [preferences, setPreferences] = useState('');
  const [contactPreference, setContactPreference] = useState('email');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // For in-app access (authenticated users without token)
    const isInAppAccess = !token && user;
    // For external access (with token, may or may not be authenticated)
    const isExternalAccess = !!token;
    
    if (!responseId || (!isInAppAccess && !isExternalAccess)) {
      setError('Invalid or missing access parameters');
      setIsLoading(false);
      return;
    }

    // Fetch test information for display
    const fetchTestInfo = async () => {
      try {
        let query = supabase
          .from('test_responses')
          .select(`
            candidate_id,
            tests!inner (
              title,
              job_title,
              location,
              creator_id,
              profiles!tests_creator_id_fkey (
                organization
              )
            )
          `)
          .eq('id', responseId);

        // For in-app access, verify the user is the candidate
        if (!token && user) {
          query = query.eq('candidate_id', user.id);
        }

        const { data, error } = await query.single();

        if (error) throw error;

        // For in-app access, verify ownership
        if (!token && user && data?.candidate_id !== user.id) {
          throw new Error('Unauthorized access to this interview invitation');
        }

        if (data?.tests) {
          setTestInfo({
            title: data.tests.title,
            job_title: data.tests.job_title || data.tests.title,
            organization: data.tests.profiles?.organization || 'Company',
            location: data.tests.location
          });
        }
      } catch (err) {
        console.error('Error fetching test info:', err);
        setError('Unable to load job information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestInfo();
  }, [responseId, token, user]);

  const handleSubmit = async () => {
    if (selectedSlots.length === 0) {
      toast({
        title: "No availability selected",
        description: "Please select at least one available time slot.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('save-availability', {
        body: {
          responseId,
          token, // Will be null for in-app access, function should handle this
          availableSlots: selectedSlots,
          timezone,
          preferences,
          contactPreference
        }
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Availability submitted",
        description: "Thank you! Your availability has been shared with the hiring team.",
      });
    } catch (err: any) {
      console.error('Error submitting availability:', err);
      toast({
        title: "Submission failed",
        description: err.message || "Unable to submit your availability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Access Error</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Availability Submitted</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for sharing your availability! The hiring team will contact you soon to schedule your interview.
            </p>
            <Badge variant="secondary" className="text-sm">
              {testInfo?.organization} • {testInfo?.job_title}
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Calendar className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Interview Availability</h1>
          </div>
          {testInfo && (
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">{testInfo.job_title}</h2>
              <div className="flex items-center justify-center gap-4 text-muted-foreground">
                <span>{testInfo.organization}</span>
                <span>•</span>
                <span>{testInfo.location}</span>
              </div>
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Calendar Section */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Select Your Available Times
                </CardTitle>
                <CardDescription>
                  Choose the time slots when you're available for an interview over the next two weeks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AvailabilityCalendar
                  selectedSlots={selectedSlots}
                  onSlotsChange={setSelectedSlots}
                  timezone={timezone}
                />
              </CardContent>
            </Card>
          </div>

          {/* Details Section */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Interview Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Timezone</label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Chicago">Central Time</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                      <SelectItem value="Europe/London">London</SelectItem>
                      <SelectItem value="Europe/Paris">Central European Time</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                      <SelectItem value="Asia/Kolkata">India Standard Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Preferred Contact Method</label>
                  <Select value={contactPreference} onValueChange={setContactPreference}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Additional Notes (Optional)</label>
                  <Textarea
                    placeholder="Any specific preferences or requirements for the interview..."
                    value={preferences}
                    onChange={(e) => setPreferences(e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Selected Times</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedSlots.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No time slots selected</p>
                ) : (
                  <div className="space-y-2">
                    {selectedSlots.map((slot, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div>
                          <p className="font-medium">{new Date(slot.date).toLocaleDateString()}</p>
                          <p className="text-sm text-muted-foreground">
                            {slot.startTime} - {slot.endTime}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || selectedSlots.length === 0}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Availability'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};